import React, { useCallback } from 'react';
import { ArrowForward, ArrowBack } from '@styled-icons/material';
import { CARDS_PER_PAGE } from './scrollControl.constants';
import { useScrollGestureState } from './scrollGestureState';
import { useDragToScroll } from './useDragToScroll';
import { useHorizontalWheelScroll } from './useHorizontalWheelScroll';
import { useScrollPaging } from './useScrollPaging';
import * as S from './ScrollControl.styles';

type ScrollControlProps = {
  scrollItems: string[];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
};

// Three input paths move the caller's container — drag, wheel, these buttons — one hook each.
const ScrollControl = ({ scrollItems, scrollContainerRef }: ScrollControlProps) => {
  const gestureState = useScrollGestureState();

  useDragToScroll(scrollContainerRef, gestureState);
  useHorizontalWheelScroll(scrollContainerRef, gestureState);
  const { startIndex, scrollToIndex } = useScrollPaging(scrollContainerRef, gestureState, scrollItems.length);

  const itemCount = scrollItems.length;
  const inViewEndIndex = startIndex + CARDS_PER_PAGE;
  const canScrollBackward = startIndex > 0;
  const canScrollForward = startIndex + CARDS_PER_PAGE < itemCount;

  const handleScrollForward = useCallback(() => {
    scrollToIndex(startIndex + CARDS_PER_PAGE);
  }, [scrollToIndex, startIndex]);

  const handleScrollBackward = useCallback(() => {
    scrollToIndex(startIndex - CARDS_PER_PAGE);
  }, [scrollToIndex, startIndex]);

  return (
    <S.ScrollContainer>
      <S.BackScrollButton
        disabled={!canScrollBackward}
        color="secondary"
        shade="light"
        onClick={handleScrollBackward}
        type="button"
        aria-label="Forrige"
      >
        <ArrowBack width={20} />
      </S.BackScrollButton>
      {scrollItems.map((key, index) => (
        <S.Dot key={`${key}_dot`} $inView={index >= startIndex && index < inViewEndIndex} />
      ))}
      <S.ForwardScrollButton
        disabled={!canScrollForward}
        color="secondary"
        shade="light"
        type="button"
        onClick={handleScrollForward}
        aria-label="Neste"
      >
        <ArrowForward width={20} />
      </S.ForwardScrollButton>
    </S.ScrollContainer>
  );
};

export default ScrollControl;
